import { useState } from 'react'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { SubscriptionPlanType } from '@/types'
import { SUBSCRIPTION_PLANS } from '@/constants'

// Format price for display
export const formatPrice = (plan: string, interval: 'monthly' | 'yearly') => {
  const planConfig = SUBSCRIPTION_PLANS[plan.toUpperCase()]
  if (!planConfig?.pricing?.[interval]) {
    return { price: 'Free', period: '' }
  }

  const pricing = planConfig.pricing[interval]
  return {
    price: `$${pricing.price}`,
    period: pricing.interval,
  }
}

// Billing interval toggle component
export const BillingToggle = ({
  billingInterval,
  setBillingInterval,
}: {
  billingInterval: 'monthly' | 'yearly'
  setBillingInterval: (interval: 'monthly' | 'yearly') => void
}) => {
  const toggleBillingInterval = () => {
    setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')
  }

  return (
    <div className="flex justify-center mb-8">
      <div className="flex items-center bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
        <span
          className={`mr-2 ${billingInterval === 'monthly' ? 'font-bold' : ''} dark:text-white`}
        >
          Monthly
        </span>
        <div
          className="relative inline-block w-12 h-6 cursor-pointer"
          onClick={toggleBillingInterval}
        >
          <div
            className={`w-12 h-6 rounded-full transition-colors duration-200 ${billingInterval === 'yearly' ? 'bg-green-500' : 'bg-gray-400'}`}
          ></div>
          <div
            className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 transform ${billingInterval === 'yearly' ? 'translate-x-6' : ''}`}
          ></div>
        </div>
        <span
          className={`ml-2 ${billingInterval === 'yearly' ? 'font-bold' : ''} dark:text-white`}
        >
          Yearly{' '}
          <span className="text-xs text-green-600 dark:text-green-400 font-bold">
            Save ~40%
          </span>
        </span>
      </div>
    </div>
  )
}

interface PricingPlansProps {
  currentPlan?: SubscriptionPlanType | null
  billingInterval: 'monthly' | 'yearly'
  onUpgradeClick: (plan: SubscriptionPlanType) => void
}

export const PricingPlans = ({
  currentPlan = SubscriptionPlanType.Free,
  billingInterval,
  onUpgradeClick,
}: PricingPlansProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Free Plan */}
      <Card
        title="Basic"
        subTitle="Free"
        className="h-full dark:bg-gray-800 dark:border-gray-700"
      >
        <div className="h-64 flex flex-col justify-between">
          <div>
            <h3 className="font-medium mb-2">Features:</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Up to 5 active reminders</li>
              <li>7-day message history</li>
              <li className="text-gray-400 dark:text-gray-500 line-through">
                No attachments
              </li>
              <li className="text-gray-400 dark:text-gray-500 line-through">
                No recurring reminders
              </li>
              <li className="text-gray-400 dark:text-gray-500 line-through">
                No message templates
              </li>
            </ul>
          </div>

          <Button
            label={
              currentPlan === SubscriptionPlanType.Free && currentPlan !== null
                ? 'Current Plan'
                : 'Get Started Free'
            }
            className="p-button-outlined"
            disabled={currentPlan === SubscriptionPlanType.Free && currentPlan !== null}
            onClick={() => onUpgradeClick(SubscriptionPlanType.Free)}
            style={{ 
              display: currentPlan !== SubscriptionPlanType.Free && 
                      currentPlan !== null &&
                      (currentPlan === SubscriptionPlanType.Pro || 
                       currentPlan === SubscriptionPlanType.Team || 
                       currentPlan === SubscriptionPlanType.Gift) 
                      ? 'none' : 'inline-flex' 
            }}
          />
        </div>
      </Card>

      {/* Pro Plan */}
      <Card
        title="Pro"
        subTitle={`${formatPrice('Pro', billingInterval).price}/${formatPrice('Pro', billingInterval).period}`}
        className={`h-full relative dark:bg-gray-800 dark:border-gray-700 ${currentPlan === SubscriptionPlanType.Pro ? 'border-2 border-green-500 dark:border-green-400' : ''}`}
      >
        <div className="absolute -top-3 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
          MOST POPULAR
        </div>
        <div className="h-64 flex flex-col justify-between">
          <div>
            <h3 className="font-medium mb-2">Features:</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Unlimited reminders</li>
              <li>Attachments support (up to 10)</li>
              <li>90-day message history</li>
              <li>Recurring reminders</li>
              <li>Message templates</li>
              <li>Best for individuals</li>
            </ul>
          </div>

          <Button
            label={
              currentPlan === SubscriptionPlanType.Pro && currentPlan !== null
                ? 'Current Plan'
                : 'Start 30-day Free Trial'
            }
            icon={currentPlan !== SubscriptionPlanType.Pro || currentPlan === null ? 'pi pi-arrow-up' : undefined}
            className={
              currentPlan === SubscriptionPlanType.Pro && currentPlan !== null
                ? 'p-button-success'
                : 'p-button-primary p-button-raised'
            }
            disabled={currentPlan === SubscriptionPlanType.Pro && currentPlan !== null}
            onClick={() => onUpgradeClick(SubscriptionPlanType.Pro)}
          />
        </div>
      </Card>

      {/* Team Plan */}
      <Card
        title="Team"
        subTitle={`${formatPrice('Team', billingInterval).price}/user/${formatPrice('Team', billingInterval).period}`}
        className={`h-full dark:bg-gray-800 dark:border-gray-700 ${currentPlan === SubscriptionPlanType.Team ? 'border-2 border-green-500 dark:border-green-400' : ''}`}
      >
        <div className="h-64 flex flex-col justify-between">
          <div>
            <h3 className="font-medium mb-2">Features:</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Everything in Pro plan</li>
              <li>Minimum 2 users</li>
              <li>Central billing & management</li>
              <li>Priority support</li>
            </ul>
          </div>

          <Button
            label={
              currentPlan === SubscriptionPlanType.Team && currentPlan !== null
                ? 'Current Plan'
                : 'Start 30-day Free Trial'
            }
            icon={currentPlan !== SubscriptionPlanType.Team || currentPlan === null ? 'pi pi-users' : undefined}
            className={
              currentPlan === SubscriptionPlanType.Team && currentPlan !== null
                ? 'p-button-success'
                : 'p-button-primary p-button-raised'
            }
            disabled={currentPlan === SubscriptionPlanType.Team && currentPlan !== null}
            onClick={() => onUpgradeClick(SubscriptionPlanType.Team)}
          />
        </div>
      </Card>
    </div>
  )
}
